import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { ThemeMenu } from '../components/AppHeader';
import { BrandMark } from '../components/BrandMark';
import { CodeTicket } from '../components/CodeTicket';
import { Button } from '../components/ui/Button';
import { Field, FormError, Input } from '../components/ui/Field';
import { ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Link, navigate, useLocation } from '../router';

/** Only same-site paths are allowed as a post-sign-in destination. */
function safeNext(raw: string | null): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
}

export function SignIn() {
  const { user, signIn, changePassword } = useAuth();
  const { search } = useLocation();
  const next = safeNext(search.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'credentials' | 'new-password'>('credentials');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwError, setPwError] = useState<{ next?: string; confirm?: string }>({});

  // Already signed in (and not forced to change password): go on.
  useEffect(() => {
    if (user && !user.mustChangePassword && step === 'credentials') navigate(next, { replace: true });
    if (user?.mustChangePassword && step === 'credentials' && !password) setStep('new-password');
  }, [user, next, step, password]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      const signedIn = await signIn(email.trim(), password);
      if (signedIn.mustChangePassword) setStep('new-password');
      else navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNew = async (e: FormEvent) => {
    e.preventDefault();
    const problems: typeof pwError = {};
    if (Array.from(newPassword).length < 10) problems.next = 'Use at least 10 characters.';
    if (newPassword !== confirm) problems.confirm = "The passwords don't match.";
    setPwError(problems);
    if (Object.keys(problems).length) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(password, newPassword);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') setPwError({ next: err.message });
      else if (err instanceof ApiError && err.code === 'invalid_password') {
        setError('Please sign in again to set your password.');
        setStep('credentials');
      } else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex flex-col px-4 sm:px-8">
        <header className="flex h-16 items-center justify-between">
          <Link to="/" className="rounded-md" aria-label="AMG Support home">
            <BrandMark />
          </Link>
          <ThemeMenu />
        </header>
        <main id="main" className="grid flex-1 place-items-center py-10">
          <div className="w-full max-w-[24rem] animate-rise">
            {step === 'credentials' ? (
              <>
                <h1 className="text-title font-semibold">Staff sign in</h1>
                <p className="mt-2 text-compact text-ink-3">Use the account your AMG administrator created for you.</p>
                <form onSubmit={submit} noValidate className="mt-8 flex flex-col gap-5">
                  <FormError>{error}</FormError>
                  <Field label="Work email">
                    {(p) => <Input {...p} type="email" autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}
                  </Field>
                  <Field label="Password">
                    {(p) => <Input {...p} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
                  </Field>
                  <Button type="submit" variant="primary" size="lg" block loading={busy} iconRight={<ArrowRight aria-hidden className="size-4" />}>
                    Sign in
                  </Button>
                </form>
                <p className="mt-8 border-t border-line pt-6 text-compact text-ink-3">
                  Joining a session? You don't need an account.{' '}
                  <Link to="/" className="font-medium text-accent-text underline decoration-accent-line underline-offset-4 hover:decoration-current">
                    Enter a code
                  </Link>
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep('credentials')}
                  className="mb-6 inline-flex cursor-pointer items-center gap-1.5 rounded-sm text-label font-medium text-ink-3 hover:text-ink"
                >
                  <ArrowLeft aria-hidden className="size-4" /> Back
                </button>
                <h1 className="text-title font-semibold">Choose your own password</h1>
                <p className="mt-2 text-compact text-ink-3">
                  You signed in with a one-time password. Set a new one to start hosting sessions.
                </p>
                <form onSubmit={submitNew} noValidate className="mt-8 flex flex-col gap-5">
                  <FormError>{error}</FormError>
                  <Field label="New password" hint="At least 10 characters. A few unrelated words work well." error={pwError.next}>
                    {(p) => <Input {...p} type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />}
                  </Field>
                  <Field label="Confirm new password" error={pwError.confirm}>
                    {(p) => <Input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
                  </Field>
                  <Button type="submit" variant="primary" size="lg" block loading={busy}>
                    Save password and continue
                  </Button>
                </form>
              </>
            )}
          </div>
        </main>
      </div>

      <aside aria-hidden className="relative hidden overflow-hidden bg-[#8a3b5e] lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_100%_0%,rgb(255_255_255/0.10),transparent_60%)]" />
        <p className="relative text-meta font-semibold uppercase tracking-[0.14em] text-white/70">AMG Support</p>
        <div className="relative">
          <p className="max-w-[26rem] font-display text-[3.25rem] leading-[1.02] text-white">Support that shows, not just tells.</p>
          <div className="mt-10 w-fit rounded-xl bg-white p-5 text-[#1b1918] shadow-[0_30px_60px_-20px_rgb(0_0_0/0.45)]" data-theme="light">
            <p className="mb-3 text-meta font-semibold uppercase tracking-[0.1em] text-[#5f5954]">Your session code</p>
            <CodeTicket code="K7QM2PX9RD" size="md" />
            <p className="mt-3 flex items-center gap-1.5 text-meta text-[#5f5954]">
              <Clock aria-hidden className="size-3.5" /> Valid for 15 minutes · you approve everyone who joins
            </p>
          </div>
        </div>
        <p className="relative max-w-sm text-compact text-white/75">Start a session, read out the code, and help people see the same screen in seconds.</p>
      </aside>
    </div>
  );
}
